import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { X, Download, FilePdf, Wrench, Plus, ShieldCheck, SealCheck, User, CheckCircle, CalendarBlank, BellRinging } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "./ui/dialog";
import { Spinner } from "./ui-bits";
import { api, API, formatDate, scoreColor } from "../lib/api";

const CredIcon = ({ weight }) => {
  if (weight === 5) return <ShieldCheck size={15} weight="fill" className="text-score-green" />;
  if (weight === 3) return <SealCheck size={15} weight="fill" className="text-gold" />;
  return <User size={15} weight="light" className="text-navy/40" />;
};

export const PassportDetailModal = ({ detail, merchantId, onClose }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [score, setScore] = useState(detail.provenanceScore);
  const [showForm, setShowForm] = useState(false);
  const [desc, setDesc] = useState("");
  const [provider, setProvider] = useState("");
  const [saving, setSaving] = useState(false);
  const [warranty, setWarranty] = useState(detail.warrantyExpiresAt ? detail.warrantyExpiresAt.slice(0, 10) : "");
  const [savingW, setSavingW] = useState(false);
  const [reminding, setReminding] = useState(false);

  const loadEvents = async () => {
    try {
      const { data } = await api.get(`/dpp/${detail.certificateId}/events?merchant_id=${merchantId}`);
      setEvents(data.events);
    } catch (err) {
      console.error("Failed to load passport events", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addService = async (e) => {
    e.preventDefault();
    if (!desc.trim()) return toast.error("Add a description");
    setSaving(true);
    try {
      const { data } = await api.post("/dpp/service", {
        certificate_id: detail.certificateId,
        merchant_id: merchantId,
        description: desc.trim(),
        performed_by: provider.trim() || null,
      });
      setScore(data.provenanceScore);
      setDesc(""); setProvider(""); setShowForm(false);
      toast.success("Service record added");
      loadEvents();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not add record");
    } finally {
      setSaving(false);
    }
  };

  const saveWarranty = async () => {
    setSavingW(true);
    try {
      await api.put("/dpp/warranty", {
        certificate_id: detail.certificateId,
        merchant_id: merchantId,
        warranty_expires_at: warranty ? new Date(warranty).toISOString() : null,
      });
      toast.success(warranty ? "Warranty date saved" : "Warranty cleared");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save");
    } finally {
      setSavingW(false);
    }
  };

  const remind = async () => {
    setReminding(true);
    try {
      const { data } = await api.post("/dpp/warranty/remind", { certificate_id: detail.certificateId, merchant_id: merchantId });
      toast.success(`Reminder sent to ${data.sentTo}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send reminder");
    } finally {
      setReminding(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md p-0 overflow-hidden bg-white rounded-2xl border-navy/10 max-h-[92vh] flex flex-col" data-testid="passport-detail-modal">
        <DialogTitle className="sr-only">Passport {detail.certificateId}</DialogTitle>
        <DialogDescription className="sr-only">Passport details, service history and downloads.</DialogDescription>

        <div className="bg-navy px-7 py-5 flex items-center justify-between shrink-0">
          <span className="text-cream text-sm uppercase tracking-[0.15em]">Passport</span>
          <button onClick={onClose} className="text-cream/60 hover:text-cream transition-colors"><X size={20} /></button>
        </div>

        <div className="p-7 overflow-y-auto">
          <div className="text-center">
            <p className="text-navy font-medium">{detail.productName}</p>
            <p className="text-xs text-navy/40 font-mono mt-1">{detail.certificateId}</p>
            <div className="mt-5 bg-cream rounded-2xl p-5">
              <img src={detail.qrCode} alt="QR" className="mx-auto h-40 w-40" />
            </div>
            <div className="mt-4 flex items-center justify-center gap-2">
              <span className="inline-flex items-center gap-1.5 text-sm font-medium" style={{ color: scoreColor(score) }}>
                <span className="h-2 w-2 rounded-full" style={{ background: scoreColor(score) }} /> {score}
              </span>
              <span className="text-navy/30">·</span>
              <span className="text-navy/50 text-xs">Issued {formatDate(detail.issuedAt)}</span>
            </div>
          </div>

          <div className="mt-5 grid grid-cols-2 gap-2">
            <a href={`${API}/qr/${detail.certificateId}?format=png`} data-testid="download-qr-detail" className="flex items-center justify-center gap-2 bg-navy text-cream rounded-xl py-3 text-xs hover:brightness-110 transition-all">
              <Download size={15} weight="light" /> QR Code
            </a>
            <a href={`${API}/certificate/${detail.certificateId}.pdf`} data-testid="download-certificate-pdf" className="flex items-center justify-center gap-2 bg-gold text-navy rounded-xl py-3 text-xs font-medium hover:brightness-105 transition-all">
              <FilePdf size={15} weight="light" /> Certificate PDF
            </a>
          </div>

          {/* Service history */}
          <div className="mt-7">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm text-navy flex items-center gap-2"><Wrench size={16} weight="light" /> Provenance & Service</h3>
              {!showForm && (
                <button data-testid="add-service-button" onClick={() => setShowForm(true)} className="flex items-center gap-1 text-xs text-gold hover:text-navy transition-colors">
                  <Plus size={14} weight="bold" /> Add record
                </button>
              )}
            </div>

            {showForm && (
              <form onSubmit={addService} className="bg-cream rounded-xl p-4 mb-4 space-y-2" data-testid="service-form">
                <input data-testid="service-desc-input" value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="e.g. Full service & authentication" className="w-full bg-white border border-navy/10 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-gold transition-colors text-navy" />
                <input data-testid="service-provider-input" value={provider} onChange={(e) => setProvider(e.target.value)} placeholder="Provider (optional)" className="w-full bg-white border border-navy/10 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-gold transition-colors text-navy" />
                <div className="flex gap-2 pt-1">
                  <button type="submit" disabled={saving} data-testid="service-submit-button" className="flex-1 flex items-center justify-center gap-2 bg-navy text-cream rounded-lg py-2.5 text-xs hover:brightness-110 transition-all disabled:opacity-60">
                    {saving ? <Spinner className="h-3.5 w-3.5" /> : <CheckCircle size={14} weight="fill" />} Save
                  </button>
                  <button type="button" onClick={() => setShowForm(false)} className="px-4 text-xs text-navy/50 hover:text-navy transition-colors">Cancel</button>
                </div>
              </form>
            )}

            {loading ? (
              <div className="py-6 flex justify-center"><Spinner className="h-6 w-6" /></div>
            ) : (
              <ol className="relative border-l border-navy/10 ml-1.5">
                {events.map((e) => (
                  <li key={e.id} className="ml-5 pb-5 last:pb-0">
                    <span className="absolute -left-[7px] mt-1 h-3.5 w-3.5 rounded-full bg-white border-2" style={{ borderColor: e.weight === 5 ? "#2D6A4F" : e.weight === 3 ? "#C9A84C" : "#94a3b8" }} />
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <CredIcon weight={e.weight} />
                      <span className="text-navy text-sm font-medium capitalize">{e.type.replace(/_/g, " ")}</span>
                      <span className="text-[0.55rem] uppercase tracking-wide text-navy/50 bg-cream rounded-full px-1.5 py-0.5">{e.credibility}</span>
                    </div>
                    <p className="text-xs text-navy/60 mt-0.5">{e.description}</p>
                    <p className="text-[0.68rem] text-navy/35 mt-0.5">{formatDate(e.date)}</p>
                  </li>
                ))}
              </ol>
            )}
          </div>

          <div className="mt-6 border-t border-navy/10 pt-5">
            <h3 className="text-sm text-navy flex items-center gap-2 mb-3"><CalendarBlank size={16} weight="light" /> Warranty</h3>
            <div className="flex gap-2">
              <input type="date" data-testid="warranty-date-input" value={warranty} onChange={(e) => setWarranty(e.target.value)} className="flex-1 bg-cream border border-navy/10 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-gold transition-colors text-navy" />
              <button data-testid="warranty-save-button" onClick={saveWarranty} disabled={savingW} className="px-5 bg-navy text-cream rounded-lg text-xs hover:brightness-110 transition-all disabled:opacity-60">{savingW ? "…" : "Save"}</button>
            </div>
            <button data-testid="warranty-remind-button" onClick={remind} disabled={reminding} className="mt-2 w-full flex items-center justify-center gap-1.5 text-xs text-gold hover:text-navy transition-colors disabled:opacity-60">
              <BellRinging size={14} weight="light" /> {reminding ? "Sending…" : "Send reminder to owner now"}
            </button>
          </div>

          <a href={detail.verificationUrl} target="_blank" rel="noreferrer" className="mt-4 block text-center text-xs text-navy/50 hover:text-gold transition-colors">Open public verification page ↗</a>
        </div>
      </DialogContent>
    </Dialog>
  );
};
